import {ReactNode} from "react";
import {Box} from "@chakra-ui/react";
import type {ReviewableId, ReviewAction} from "platformed-client/types";
import {q, useQueryData} from "../../../state";
import {DrawerBody, DrawerTitle} from "../../../components/ui/drawer";
import {DrawerPageHeader} from "../../TPRM/Engagement/ProcessesDemo/components/DrawerPageHeader";
import {InnerDrawerProps, withDrawer} from "../../../state/withDrawer";
import {ReviewItem} from ".";
import {ImmediateReviewSections} from "./ImmediateReviewSections";
import {useReviewSource} from "./ReviewSource";
import T from "platformed-components/i18n/T";
import {ReviewerNoteContext} from "./reviewerNote";

/** Loads the entity's reviewable, then renders the reviewers. */
const ReviewsLoaded = ({reviewableId, ...rest}: {reviewableId: ReviewableId} & Omit<ReviewsBodyProps, "reviews">) => {
  const reviewable = useQueryData(q.reviewable(reviewableId));
  return <ReviewsBody reviews={reviewable?.reviews ?? {}} {...rest} />;
};

type ReviewsBodyProps = {
  reviews: Parameters<typeof ImmediateReviewSections>[0]["reviews"];
  reviewItem: ReviewItem;
  targetName: string;
  descendantPageCount: number;
};

const ReviewsBody = ({reviews, reviewItem, targetName, descendantPageCount}: ReviewsBodyProps) => {
  const roles = useQueryData(q.tprm.reviewerRoles());
  return (
    <Box px={6} py={5}>
      <ImmediateReviewSections
        reviews={reviews}
        roles={roles}
        reviewItem={reviewItem}
        targetName={targetName}
        descendantPageCount={descendantPageCount}
      />
    </Box>
  );
};

/**
 * Entity-agnostic reviewers drawer. Keyed on the generic `reviewable_id` (the
 * polymorphic review subsystem) plus a {@link ReviewItem} that routes review
 * mutations to the correct API. Any entity that owns a reviewable can use this
 * — audit plan pages today, populations / others as they gain reviewables.
 */
export const ReviewsDrawer = withDrawer(
  ({
    reviewableId,
    reviewItem,
    targetName,
    descendantPageCount = 0,
    reviewerNote,
  }: InnerDrawerProps & {
    reviewableId?: ReviewableId | null;
    reviewItem: ReviewItem;
    targetName: string;
    /** Pages beneath this one, offered as a cascade target. */
    descendantPageCount?: number;
    reviewerNote?: (review: ReviewAction) => ReactNode;
  }) => {
    const source = useReviewSource();
    return (
      <ReviewerNoteContext.Provider value={reviewerNote ?? null}>
        <DrawerPageHeader
          title={
            <DrawerTitle>
              <T keyName="c.Reviews" />
            </DrawerTitle>
          }
        />
        <DrawerBody p={0}>
          {source ? (
            <ReviewsBody
              reviews={source.reviews}
              reviewItem={reviewItem}
              targetName={targetName}
              descendantPageCount={descendantPageCount}
            />
          ) : reviewableId ? (
            <ReviewsLoaded
              reviewableId={reviewableId}
              reviewItem={reviewItem}
              targetName={targetName}
              descendantPageCount={descendantPageCount}
            />
          ) : (
            <ReviewsBody
              reviews={{}}
              reviewItem={reviewItem}
              targetName={targetName}
              descendantPageCount={descendantPageCount}
            />
          )}
        </DrawerBody>
      </ReviewerNoteContext.Provider>
    );
  },
  {placement: "end", size: "lg"},
);
